const OBREF_COOKIE = '__obref';
const OPPREF_COOKIE = '__oppref';

export interface OpenAiCookies {
    obref?: string;
    oppref?: string;
}

/**
 * Reads the ChatGPT Ads first-party attribution cookies so they can be
 * forwarded to the Conversions API alongside the event.
 *
 * Both are written by the Measurement Pixel and neither is hashed. The pixel
 * only populates them for its own browser events, so the Conversions API sees
 * them only if they are read here and sent with the server event:
 *
 * - `__obref` is an opaque browser reference, sent as `user.obref`.
 * - `__oppref` is the click reference OpenAI appends to the landing page URL,
 *   which the pixel persists across page views. It is sent at the top level of
 *   the event, not inside `user`. Without it a server conversion carries no
 *   click identifier and cannot be attributed to the ad that earned it.
 *
 * Both are absent until someone arrives from a ChatGPT ad, and on any site
 * where the pixel is not configured.
 */
export function getOpenAiCookies(): OpenAiCookies {
    if (typeof document === 'undefined') {
        return {};
    }

    return {
        obref: readCookie(OBREF_COOKIE),
        oppref: readCookie(OPPREF_COOKIE),
    };
}

function readCookie(name: string): string | undefined {
    const prefix = `${name}=`;

    for (const part of document.cookie.split(';')) {
        const cookie = part.trim();
        if (cookie.startsWith(prefix)) {
            return decodeURIComponent(cookie.slice(prefix.length)) || undefined;
        }
    }

    return undefined;
}
